import { ChangeEvent, useCallback, useEffect, useState } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { useKeyPress } from "@/core/keyPressManager";
import { Layer } from "@/domains/chat/constants/layer";
import {
  indexMessageBySearchSelector,
  setIndexMessageBySearch,
  setOpenSearch,
  setSearchValue,
  setUidMessagesBySearch,
  uidMessagesBySearchSelector,
} from "@/domains/chat/slice";
import { currLayerSelector } from "@/domains/layer/slice";
import KeyCode from "@/assets/constants/key-code";

export default function useSearchEvent() {
  const dispatch = useAppDispatch();
  const currLayer = useAppSelector(currLayerSelector);
  const uidMessagesBySearch = useAppSelector(uidMessagesBySearchSelector);
  const indexMessageBySearch = useAppSelector(indexMessageBySearchSelector);
  const { subscribeKeyDown, unsubscribeKeyDown } = useKeyPress();
  const [isFocus, setIsFocus] = useState(false);

  const onCloseSearch = useCallback(() => {
    dispatch(setOpenSearch(false));
    dispatch(setSearchValue(""));
    dispatch(setIndexMessageBySearch(-1));
    dispatch(setUidMessagesBySearch([]));
  }, [dispatch]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(setSearchValue(e.target.value));
    },
    [dispatch],
  );

  const upSearchIndex = useCallback(() => {
    if (indexMessageBySearch + 1 < uidMessagesBySearch.length) {
      dispatch(setIndexMessageBySearch(indexMessageBySearch + 1));
    }
  }, [indexMessageBySearch, uidMessagesBySearch, dispatch]);

  const downSearchIndex = useCallback(() => {
    if (indexMessageBySearch > -1) {
      dispatch(setIndexMessageBySearch(indexMessageBySearch - 1));
    }
  }, [indexMessageBySearch, dispatch]);

  const onFocus = useCallback(() => {
    setIsFocus(true);
  }, []);

  const onBlur = useCallback(() => {
    setIsFocus(false);
  }, []);

  const onKeyDown = useCallback(
    (keyCode: KeyCode) => {
      if (currLayer !== Layer.CHAT) return;
      if (!isFocus) return;

      switch (keyCode) {
        case KeyCode.UP_ARROW: {
          upSearchIndex();
          break;
        }
        case KeyCode.DOWN_ARROW: {
          downSearchIndex();
          break;
        }
        case KeyCode.ESCAPE: {
          onCloseSearch();
          break;
        }
        default:
          break;
      }
    },
    [isFocus, currLayer, downSearchIndex, onCloseSearch, upSearchIndex],
  );

  useEffect(() => {
    const key = "searchInput";
    subscribeKeyDown(key, onKeyDown);
    return () => {
      unsubscribeKeyDown(key);
    };
  }, [onKeyDown, subscribeKeyDown, unsubscribeKeyDown]);

  return {
    downSearchIndex,
    upSearchIndex,
    onKeyDown,
    onChange,
    onCloseSearch,
    onFocus,
    onBlur,
  };
}
